import { PushContentDefinition } from 'modules/graphql/types';
import { BUTTON_VARIANTS } from 'ui/button';
import { Android24, Apple24, InfoFill, AnimatedRefreshIcon } from 'ui/icons';
import { SegmentedControl } from 'ui/segmentedControl';
import { BASIC_SELECT_SIZES, BasicSelect } from 'ui/selects';
import { spacing } from 'ui/theme';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';

import { Preview } from './Preview';
import {
  PreviewContainer,
  PreviewContent,
  PreviewControls,
  PreviewFooter,
  PreviewMessage,
  PreviewPaneContainer,
  PreviewRefreshButton,
  RawDataContainer,
  RawDataHeader,
} from '../../common';
import { displayContent } from '../../common/content';
import { OS, PushPreviewType, TemplateEditViews } from '../../common/types';
import { hasHandlebars } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { usePushPreviewContext } from '../context/PushPreviewContext';
import { PreviewHeader } from '../../common/PreviewHeader';
import { RawPreviewField } from '../../common/RawPreviewField';
import {
  TEMPLATE_EDITOR_PUSH_PREVIEW_CHAR_COUNT,
  TEMPLATE_EDITOR_PUSH_PREVIEW_DATA_MESSAGE,
  TEMPLATE_EDITOR_PUSH_PREVIEW_FOOTER,
  TEMPLATE_EDITOR_PUSH_PREVIEW_PANE_ID,
  TEMPLATE_EDITOR_PUSH_PREVIEW_REFRESH_BUTTON,
  TEMPLATE_EDITOR_PUSH_PREVIEW_SEG_COUNT,
  TEMPLATE_EDITOR_PUSH_PREVIEW_SEGMENTED_CONTROL,
  TEMPLATE_EDITOR_PUSH_PREVIEW_TYPE_SELECTOR,
} from '../constants';
import { SETTINGS_DISPLAY_CONTENT } from '../../common/constants';

export const previewTypeOptions = Object.entries(PushPreviewType).map((pair) => ({
  id: pair[0],
  label: pair[1],
  value: pair[1],
}));

export const PreviewPane = () => {
  const { view } = useQueryParamContext();
  const {
    isLoading: isRefreshing,
    messageContentBody,
    messageRawDataPayload,
    messageContentTitle,
    onRefreshPreview,
  } = usePushPreviewContext();
  const { liveTemplate, hasContent } = useEditableFieldsContext();
  const [selectedOs, setSelectedOs] = useLocalStorage<OS>('pushPreviewOs', OS.ios);
  const [selectedPreviewType, setSelectedPreviewType] = useLocalStorage<PushPreviewType>(
    'pushPreviewType',
    PushPreviewType.notification,
  );
  const pushData = (liveTemplate?.mediumData || {}) as PushContentDefinition;

  const isDisabled = !hasContent;
  const isPreviewWithData = view === TemplateEditViews.preview;
  const body = isPreviewWithData ? messageContentBody : pushData.message;
  const title = isPreviewWithData ? messageContentTitle : pushData.title;
  const hasDataParamsInBody = !isPreviewWithData && hasHandlebars(body);
  const hasDataParamsInTitle = !isPreviewWithData && hasHandlebars(title);

  return (
    <PreviewPaneContainer>
      <PreviewContainer
        data-test={TEMPLATE_EDITOR_PUSH_PREVIEW_PANE_ID}
        isPreviewWithData={isPreviewWithData}
      >
        <PreviewHeader />
        <PreviewContent $height={'600px'} testId="template-editor-push-preview-content">
          <Preview selectedOs={selectedOs} selectedPreviewType={selectedPreviewType} />
        </PreviewContent>
        {isPreviewWithData && (
          <PreviewRefreshButton
            onClick={() => onRefreshPreview()}
            variant={BUTTON_VARIANTS.SECONDARY}
            data-test={TEMPLATE_EDITOR_PUSH_PREVIEW_REFRESH_BUTTON}
          >
            <AnimatedRefreshIcon $isAnimating={isRefreshing} />
          </PreviewRefreshButton>
        )}
        <PreviewFooter data-test={TEMPLATE_EDITOR_PUSH_PREVIEW_FOOTER}>
          <PreviewControls>
            <SegmentedControl
              onSelect={setSelectedOs}
              optionMinWidth={spacing.double}
              options={[
                {
                  disabled: isDisabled,
                  Icon: Apple24,
                  key: OS.ios,
                  value: OS.ios,
                },
                {
                  disabled: isDisabled,
                  Icon: Android24,
                  key: OS.android,
                  value: OS.android,
                },
              ]}
              value={selectedOs}
              data-test={TEMPLATE_EDITOR_PUSH_PREVIEW_SEGMENTED_CONTROL}
            />
            <BasicSelect
              data-test={TEMPLATE_EDITOR_PUSH_PREVIEW_TYPE_SELECTOR}
              defaultValues={[selectedPreviewType]}
              id="template-editor-push-preview-type-selector"
              isDisabled={isDisabled}
              onSelect={([newPreviewType]) => setSelectedPreviewType(newPreviewType)}
              options={previewTypeOptions}
              size={BASIC_SELECT_SIZES.SMALL}
            />
          </PreviewControls>
          <PreviewMessage>
            {hasDataParamsInBody || hasDataParamsInTitle ? (
              <>
                <InfoFill height={12} width={12} isColorInherited />
                <span data-test={TEMPLATE_EDITOR_PUSH_PREVIEW_DATA_MESSAGE}>
                  {displayContent.dataParamsInMessage}
                </span>
              </>
            ) : (
              <>
                <span data-test={TEMPLATE_EDITOR_PUSH_PREVIEW_CHAR_COUNT}>
                  Title: {displayContent.strCharacterLength({ charCount: title?.length || 0 })}
                </span>
                <span>|</span>
                <span data-test={TEMPLATE_EDITOR_PUSH_PREVIEW_SEG_COUNT}>
                  Body: {displayContent.strCharacterLength({ charCount: body?.length || 0 })}
                </span>
              </>
            )}
          </PreviewMessage>
        </PreviewFooter>
      </PreviewContainer>
      {isPreviewWithData && (
        <RawDataContainer>
          <RawDataHeader>{SETTINGS_DISPLAY_CONTENT.rawData.labelText}</RawDataHeader>
          <RawPreviewField populatedPayload={messageRawDataPayload} />
        </RawDataContainer>
      )}
    </PreviewPaneContainer>
  );
};

export default PreviewPane;
